import { useState, useEffect, useCallback, useMemo } from 'react';
import { invoicesService } from '../services/api';
import toast from 'react-hot-toast';

// let invoiceCache = null;
// let cacheTimestamp = 0;
// const CACHE_DURATION = 2 * 60 * 1000; // 2 minutos

export const useInvoices = () => {
  const [facturas, setFacturas] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [selectedFacturas, setSelectedFacturas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadPendingInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await invoicesService.getPending();

      if (response.data.success) {
        setFacturas(response.data.data.facturas);
        setResumen(response.data.data.resumen);
      } else {
        setError('Error cargando facturas');
        toast.error('Error cargando facturas');
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Error cargando facturas';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSummary = useCallback(async () => {
    try {
      const response = await invoicesService.getSummary();

      if (response.data.success) {
        setResumen(response.data.data);
      }
    } catch (error) {
      console.error('Error actualizando resumen:', error);
    }
  }, []);

  const toggleFactura = useCallback((factura) => {
    setSelectedFacturas(prev => {
      const index = prev.findIndex(f =>
        f.numero === factura.numero && f.sucursal === factura.sucursal
      );

      if (index >= 0) {
        return prev.filter((_, i) => i !== index);
      } else {
        return [...prev, {
          numero: factura.numero,
          sucursal: factura.sucursal,
          valor: factura.saldoCOP,
          divisaLocal: 1,
          divisaOtra: factura.cotizacionOtra || 1,
          ica: 0,
          reteIva: 0
        }];
      }
    });
  }, []);

  const selectAllFacturas = useCallback(() => {
    const allFacturas = facturas.map(factura => ({
      numero: factura.numero,
      sucursal: factura.sucursal,
      valor: factura.saldoCOP,
      divisaLocal: 1,
      divisaOtra: factura.cotizacionOtra || 1,
      ica: 0,
      reteIva: 0
    }));

    setSelectedFacturas(allFacturas);
  }, [facturas]);

  const clearSelection = useCallback(() => {
    setSelectedFacturas([]);
  }, []);

  const isFacturaSelected = useCallback((factura) => {
    return selectedFacturas.some(f =>
      f.numero === factura.numero && f.sucursal === factura.sucursal
    );
  }, [selectedFacturas]);

  const calculateSelectedTotal = useCallback(() => {
    return selectedFacturas.reduce((total, factura) => total + factura.valor, 0);
  }, [selectedFacturas]);

  const validateSelection = useCallback(() => {
    if (selectedFacturas.length === 0) {
      toast.error('Debe seleccionar al menos una factura para pagar');
      return false;
    }

    const total = calculateSelectedTotal();
    if (total <= 0) {
      toast.error('El valor total debe ser mayor a cero');
      return false;
    }

    return true;
  }, [selectedFacturas, calculateSelectedTotal]);

  useEffect(() => {
    loadPendingInvoices();
  }, [loadPendingInvoices]);

  return {
    facturas,
    resumen,
    selectedFacturas,
    loading,
    error,

    loadPendingInvoices,
    updateSummary,
    toggleFactura,
    selectAllFacturas,
    clearSelection,
    isFacturaSelected,
    calculateSelectedTotal,
    validateSelection,

    hasFacturas: facturas.length > 0,
    hasSelection: selectedFacturas.length > 0,
    selectedCount: selectedFacturas.length,
    totalSelected: calculateSelectedTotal()
  };
};